import { useState, useCallback, useEffect } from "react";
import { useAccount, useChainId, usePublicClient, useWalletClient } from "wagmi";
import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";
import { base, sepolia } from "wagmi/chains";
import { type Address, keccak256, encodePacked } from "viem";
import { explorerAddress, explorerTx, DEFAULT_CHAIN_ID } from "../lib/contract";

// Delegate addresses per chain
const DELEGATE: Record<number, Address> = {
  [base.id]: (import.meta.env.VITE_GAIA_DELEGATE_BASE ?? "0xB147331db2c6f894E7fAA5E50D6b7880438f833E") as Address,
  [sepolia.id]: (import.meta.env.VITE_GAIA_DELEGATE_SEPOLIA ?? "0x9270C41C4857D420203A6B0F2c1aC5cab7614B14") as Address,
};

function expectedDesignator(delegate: Address): `0x${string}` {
  return `0xef0100${delegate.slice(2).toLowerCase()}` as `0x${string}`;
}

function expectedCodeHash(delegate: Address): `0x${string}` {
  return keccak256(encodePacked(["bytes3", "address"], ["0xef0100", delegate]));
}

function parseAuthSignature(
  sig: any,
  delegateAddr: Address,
  chainId: number,
  nonce: number
): { address: Address; chainId: number; nonce: number; r: `0x${string}`; s: `0x${string}`; yParity: number } {
  if (typeof sig === "string" && sig.startsWith("0x") && sig.length === 132) {
    const r = `0x${sig.slice(2, 66)}` as `0x${string}`;
    const s_ = `0x${sig.slice(66, 130)}` as `0x${string}`;
    const v = parseInt(sig.slice(130, 132), 16);
    return { address: delegateAddr, chainId, nonce, r, s: s_, yParity: v >= 27 ? v - 27 : v };
  }
  if (sig && typeof sig === "object") {
    return {
      address: (sig.address ?? sig.contractAddress ?? delegateAddr) as Address,
      chainId: Number(sig.chainId ?? chainId),
      nonce: Number(sig.nonce ?? nonce),
      r: sig.r,
      s: sig.s,
      yParity: typeof sig.yParity !== "undefined" ? Number(sig.yParity) : typeof sig.v !== "undefined" ? (Number(sig.v) >= 27 ? Number(sig.v) - 27 : Number(sig.v)) : 0,
    };
  }
  throw new Error("Unexpected authorization signature format from wallet.");
}

type Stage = "idle" | "checking" | "ok" | "missing" | "wrong" | "signing" | "broadcasting" | "confirmed" | "err";

export function useDelegationStatus() {
  const { address, isConnected } = useAccount();
  const rawChainId = useChainId();
  // Defensive: some EIP-6963 providers return chainId as a string.
  const chainId = (typeof rawChainId === "string" ? parseInt(rawChainId, 10) : rawChainId) as typeof rawChainId;
  const pc = usePublicClient({ chainId: chainId || DEFAULT_CHAIN_ID });
  const targetChainId = DELEGATE[chainId ?? 0] ? chainId : DEFAULT_CHAIN_ID;
  const delegateAddr = DELEGATE[targetChainId ?? DEFAULT_CHAIN_ID];
  const expectedCode = expectedDesignator(delegateAddr);
  const expectedHash = expectedCodeHash(delegateAddr);

  const [stage, setStage] = useState<Stage>("idle");
  const [found, setFound] = useState<string | null>(null);
  const [txHash, setTxHash] = useState<string | null>(null);

  const verify = useCallback(async () => {
    if (!address || !pc) return;
    setStage("checking");
    setFound(null);
    try {
      const code = await pc.getCode({ address });
      if (!code || code === "0x") {
        setStage("missing");
        return;
      }
      setFound(code);
      if (code.toLowerCase() === expectedCode.toLowerCase()) {
        setStage("ok");
      } else {
        setStage("wrong");
      }
    } catch (e: any) {
      setStage("err");
      setFound(e instanceof Error ? e.message.split("\n")[0] : String(e));
    }
  }, [address, pc, expectedCode]);

  return {
    isConnected,
    address,
    chainId,
    targetChainId,
    delegateAddr,
    expectedCode,
    expectedHash,
    stage,
    setStage,
    found,
    setFound,
    txHash,
    setTxHash,
    verify,
  };
}

export function DelegationModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const {
    isConnected,
    address,
    chainId,
    targetChainId,
    delegateAddr,
    expectedHash,
    stage,
    setStage,
    found,
    setFound,
    txHash,
    setTxHash,
    verify,
  } = useDelegationStatus();

  const { data: walletClient } = useWalletClient();
  const pc = usePublicClient({ chainId: chainId || DEFAULT_CHAIN_ID });

  useEffect(() => {
    if (open && isConnected && stage === "idle") {
      verify();
    }
  }, [open, isConnected, stage, verify]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const handleSign = useCallback(async () => {
    if (!walletClient || !address) return;
    setStage("signing");
    setTxHash(null);

    try {
      const ethereum = (window as any).ethereum;
      if (!ethereum) {
        throw new Error("No ethereum provider found. Please use DogeShit Wallet or a 7702-compatible wallet.");
      }

      const chainIdHex = `0x${Number(targetChainId).toString(16)}`;

      try {
        const sendCallsResult = await ethereum.request({
          method: "wallet_sendCalls",
          params: [
            {
              version: "1.0",
              from: address,
              calls: [
                {
                  to: address,
                  data: "0x",
                  value: "0x0",
                },
              ],
              chainId: chainIdHex,
              capabilities: {
                [chainIdHex]: {
                  delegation: {
                    address: delegateAddr,
                  },
                },
              },
            },
          ],
        });

        if (sendCallsResult) {
          setStage("confirmed");
          setTxHash(sendCallsResult);
          setTimeout(() => verify(), 3000);
          return;
        }
      } catch (sendCallsErr: any) {
        console.log("wallet_sendCalls failed, trying fallback:", sendCallsErr);
      }

      const nonce = await pc!.getTransactionCount({ address, blockTag: "pending" });
      const authNonce = nonce + 1;
      const rawSignature = await ethereum.request({
        method: "wallet_signAuthorization",
        params: [
          {
            address: delegateAddr,
            chainId: `0x${Number(targetChainId).toString(16)}`,
            nonce: `0x${authNonce.toString(16)}`,
          },
        ],
      });

      if (rawSignature) {
        setStage("broadcasting");
        const authorization = parseAuthSignature(rawSignature, delegateAddr, targetChainId, authNonce);
        const txHashResult = await (walletClient as any).sendTransaction({
          to: address,
          data: "0x",
          value: 0n,
          authorizationList: [authorization],
        });

        if (txHashResult) {
          setStage("confirmed");
          setTxHash(txHashResult);
          setTimeout(() => verify(), 3000);
        }
      }
    } catch (e: any) {
      console.error("Delegation error:", e);
      setStage("err");
      setFound(e instanceof Error ? e.message.split("\n")[0] : String(e));
    }
  }, [walletClient, address, targetChainId, delegateAddr, pc, verify, setStage, setTxHash, setFound]);

  const handleRevoke = useCallback(async () => {
    if (!walletClient || !address) return;
    setStage("signing");
    setTxHash(null);

    try {
      const ethereum = (window as any).ethereum;
      if (!ethereum) {
        throw new Error("No ethereum provider found.");
      }

      const chainIdHex = `0x${Number(targetChainId).toString(16)}`;

      try {
        const sendCallsResult = await ethereum.request({
          method: "wallet_sendCalls",
          params: [
            {
              version: "1.0",
              from: address,
              calls: [
                {
                  to: address,
                  data: "0x",
                  value: "0x0",
                },
              ],
              chainId: chainIdHex,
              capabilities: {
                [chainIdHex]: {
                  delegation: {
                    address: "0x0000000000000000000000000000000000000000",
                  },
                },
              },
            },
          ],
        });

        if (sendCallsResult) {
          setStage("confirmed");
          setTxHash(sendCallsResult);
          setTimeout(() => verify(), 3000);
          return;
        }
      } catch (sendCallsErr: any) {
        console.log("wallet_sendCalls revoke failed, trying fallback:", sendCallsErr);
      }

      const nonce = await pc!.getTransactionCount({ address, blockTag: "pending" });
      const authNonce = nonce + 1;
      const rawSignature = await ethereum.request({
        method: "wallet_signAuthorization",
        params: [
          {
            address: "0x0000000000000000000000000000000000000000",
            chainId: `0x${Number(targetChainId).toString(16)}`,
            nonce: `0x${authNonce.toString(16)}`,
          },
        ],
      });

      if (rawSignature) {
        setStage("broadcasting");
        const authorization = parseAuthSignature(rawSignature, "0x0000000000000000000000000000000000000000" as Address, targetChainId, authNonce);
        const txHashResult = await (walletClient as any).sendTransaction({
          to: address,
          data: "0x",
          value: 0n,
          authorizationList: [authorization],
        });

        if (txHashResult) {
          setStage("confirmed");
          setTxHash(txHashResult);
          setTimeout(() => verify(), 3000);
        }
      }
    } catch (e: any) {
      console.error("Revoke error:", e);
      setStage("err");
      setFound(e instanceof Error ? e.message.split("\n")[0] : String(e));
    }
  }, [walletClient, address, targetChainId, pc, verify, setStage, setTxHash, setFound]);

  const chainName = targetChainId === base.id ? "Base" : "Sepolia";

  const modalContent = (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          onClick={onClose}
        >
          <div className="absolute inset-0 bg-ink-1000/85 backdrop-blur-lg" />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", stiffness: 400, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-[480px] card border border-white/10 shadow-[0_40px_100px_-20px_rgba(0,0,0,0.8)] overflow-hidden max-h-[85vh] flex flex-col"
          >
            <div className="px-6 py-5 border-b border-white/5 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div className="size-10 rounded-full bg-gradient-to-br from-teal-300 to-leaf grid place-items-center font-mono text-ink-1000 font-bold text-sm">
                  7702
                </div>
                <div>
                  <div className="font-display text-lg">EIP-7702 Delegation</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-bone-dim">
                    {chainName} · one signature
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="size-8 rounded-xl hover:bg-white/10 flex items-center justify-center transition-colors"
                aria-label="Close"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M3 3 L11 11 M11 3 L3 11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <div className="p-6 overflow-y-auto">
              {!isConnected ? (
                <div className="text-center py-8">
                  <div className="font-mono text-sm text-bone-dim mb-4">
                    Connect your wallet first to delegate.
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-bone-mut">
                    Use the connect button in the top right
                  </div>
                </div>
              ) : (
                <>
                  <div className="rounded-2xl bg-ink-900/60 border border-white/5 p-4 mb-5">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-bone-dim mb-2">
                      delegate contract
                    </div>
                    <a
                      href={explorerAddress(targetChainId, delegateAddr)}
                      target="_blank"
                      rel="noreferrer"
                      className="font-mono text-xs text-teal-300 hover:underline break-all"
                    >
                      {delegateAddr}
                    </a>
                    <div className="mt-2 font-mono text-[10px] text-bone-dim">
                      codehash → {expectedHash.slice(0, 18)}…
                    </div>
                  </div>

                  <div className="mb-5">
                    {stage === "checking" && (
                      <div className="flex items-center gap-3 text-bone-dim">
                        <span className="size-4 border-2 border-bone/30 border-t-bone rounded-full animate-spin" />
                        <span className="font-mono text-sm">checking on-chain…</span>
                      </div>
                    )}
                    {stage === "ok" && (
                      <div className="rounded-2xl bg-leaf/10 border border-leaf/20 p-4">
                        <div className="flex items-center gap-2 text-leaf font-mono text-sm mb-1">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M3 8 L7 12 L13 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          Your EOA is delegated
                        </div>
                        <div className="font-mono text-[10px] text-bone-dim">
                          You can now mint via Claude MCP
                        </div>
                      </div>
                    )}
                    {stage === "missing" && (
                      <div className="rounded-2xl bg-sun/10 border border-sun/20 p-4">
                        <div className="text-sun font-mono text-sm mb-1">No delegation found</div>
                        <div className="font-mono text-[10px] text-bone-dim">
                          Sign below to delegate your EOA to the mint contract.
                        </div>
                      </div>
                    )}
                    {stage === "wrong" && (
                      <div className="rounded-2xl bg-coral/10 border border-coral/20 p-4">
                        <div className="text-coral font-mono text-sm mb-1">Wrong delegation</div>
                        <div className="font-mono text-[10px] text-bone-dim break-all">{found}</div>
                        <div className="font-mono text-[10px] text-bone mt-2">
                          Re-delegate to the correct address above.
                        </div>
                      </div>
                    )}
                    {stage === "signing" && (
                      <div className="flex items-center gap-3 text-bone-dim">
                        <span className="size-4 border-2 border-bone/30 border-t-bone rounded-full animate-spin" />
                        <span className="font-mono text-sm">waiting for wallet signature…</span>
                      </div>
                    )}
                    {stage === "broadcasting" && (
                      <div className="flex items-center gap-3 text-bone-dim">
                        <span className="size-4 border-2 border-bone/30 border-t-bone rounded-full animate-spin" />
                        <span className="font-mono text-sm">broadcasting type-4 tx…</span>
                      </div>
                    )}
                    {stage === "confirmed" && (
                      <div className="rounded-2xl bg-leaf/10 border border-leaf/20 p-4">
                        <div className="flex items-center gap-2 text-leaf font-mono text-sm mb-1">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M3 8 L7 12 L13 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          Delegation confirmed
                        </div>
                        {txHash && (
                          <a
                            href={explorerTx(targetChainId, txHash)}
                            target="_blank"
                            rel="noreferrer"
                            className="font-mono text-[10px] text-teal-300 hover:underline break-all"
                          >
                            {txHash}
                          </a>
                        )}
                      </div>
                    )}
                    {stage === "err" && (
                      <div className="rounded-2xl bg-coral/10 border border-coral/20 p-4">
                        <div className="text-coral font-mono text-sm mb-1">Error</div>
                        <div className="font-mono text-[11px] text-coral/80 break-all">{found}</div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    {(stage === "missing" || stage === "wrong" || stage === "err") && (
                      <button
                        onClick={handleSign}
                        className="btn btn-primary w-full justify-center"
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="mr-2">
                          <path d="M3 8 H13 M9 4 L13 8 L9 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        sign delegation
                      </button>
                    )}

                    {stage === "ok" && (
                      <>
                        <button
                          onClick={verify}
                          className="btn btn-ghost w-full justify-center"
                        >
                          verify again
                        </button>
                        <button
                          onClick={handleRevoke}
                          className="btn w-full justify-center border border-coral/30 text-coral hover:bg-coral/10"
                        >
                          revoke delegation
                        </button>
                      </>
                    )}

                    {stage === "confirmed" && (
                      <button
                        onClick={verify}
                        className="btn btn-primary w-full justify-center"
                      >
                        verify on-chain
                      </button>
                    )}
                  </div>

                  <div className="mt-5 p-4 rounded-2xl bg-white/[0.02] border border-white/5">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-bone-dim mb-2">
                      how it works
                    </div>
                    <ol className="space-y-2 font-mono text-[11px] text-bone-dim">
                      <li className="flex gap-2">
                        <span className="text-teal-400">1.</span>
                        Sign an EIP-7702 authorization pointing your EOA at the delegate contract.
                      </li>
                      <li className="flex gap-2">
                        <span className="text-teal-400">2.</span>
                        Broadcast the type-4 transaction to activate delegation.
                      </li>
                      <li className="flex gap-2">
                        <span className="text-teal-400">3.</span>
                        Tell Claude "mint me some GAIA" — the relayer handles gas.
                      </li>
                    </ol>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
}
